"use client";

import {
  type FC,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  NavigationStateContext,
  type NavigationStateState,
  type NavigationContext,
} from "./NavigationStateContext";
import type {
  BasNavigationStateRecord,
  NavigationStateRecord,
  NavigationState,
} from "@huub-web/ui";
import {
  getMainRecordFromNavigationState,
  getParsedNavigationContextFromNavigationState,
  getPathFromNavigationState,
  getPathMapFromNavigationState,
  matchPathWithPattern,
} from "@huub-web/ui/services/navigation";
import {
  usePubSub,
  useWindowSize,
  useDebug,
  useMaybeNavigationActionInterceptor,
} from "@huub-web/ui";
import { COMPACT_LAYOUT_BREAKPOINT } from "../config/layoutConsts";
import { addParamsToURL } from "@huub-web/utils";

export type NavigationStateProviderProps = {
  navigationState: NavigationState;
  currentPath?: string;
  children: ReactNode;
  onNavigationContextChange?: (state: NavigationContext) => void;
  onManualNavigation?: (state: NavigationContext, url: string | null) => void;
  onPathNotFound?: (path: string) => void;
};

export type MenuVariant = "default" | "compact";
export type SideContentVariant = "default" | "compact";

/**
 * A provider that provides the current navigation state, and means to navigate through the app.
 */
export const NavigationStateProvider: FC<NavigationStateProviderProps> = ({
  navigationState,
  currentPath,
  onNavigationContextChange,
  onManualNavigation,
  onPathNotFound,
  children,
}) => {
  const windowSize = useWindowSize();
  const pubSub = usePubSub();
  const { log } = useDebug();
  const interceptor = useMaybeNavigationActionInterceptor();
  const interceptorRef = useRef(interceptor);
  interceptorRef.current = interceptor;

  const [navigationContext, setNavigationContext] = useState<NavigationContext>(
    [],
  );
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [isSideContentOpen, setSideContentOpen] = useState(false);

  const navigationalPathMap = useMemo(
    () => getPathMapFromNavigationState(navigationState),
    [navigationState],
  );

  const activeNavigationRecord =
    navigationContext.length === 0 ||
    !navigationState ||
    navigationState.length === 0
      ? undefined
      : getMainRecordFromNavigationState(navigationContext, navigationState);

  const activeNavigationRecords: BasNavigationStateRecord[] | undefined =
    (() => {
      if (
        navigationContext.length === 0 ||
        !navigationState ||
        navigationState.length === 0
      ) {
        return undefined;
      }

      const result: BasNavigationStateRecord[] = [];

      const record = getMainRecordFromNavigationState(
        navigationContext,
        navigationState,
      ) as NavigationStateRecord;

      result.push(record);

      // Sub step
      if (
        navigationContext?.[1] &&
        record &&
        record.steps &&
        record.steps?.length > 0
      ) {
        // First try exact match
        let subNavigationRecord = record.steps.find(
          (step) => step.id === navigationContext[1],
        );

        // If no exact match, look for dynamic placeholder
        if (!subNavigationRecord) {
          subNavigationRecord = record.steps.find(
            (step) => step.id.startsWith("[") && step.id.endsWith("]"),
          );
        }

        if (subNavigationRecord) {
          result.push(subNavigationRecord);

          // Sub sub step
          if (
            navigationContext?.[2] &&
            subNavigationRecord.steps &&
            subNavigationRecord.steps?.length > 0
          ) {
            // First try exact match
            let subSubNavigationRecord = subNavigationRecord.steps.find(
              (step) => step.id === navigationContext[2],
            );

            // If no exact match, look for dynamic placeholder
            if (!subSubNavigationRecord) {
              subSubNavigationRecord = subNavigationRecord.steps.find(
                (step) => step.id.startsWith("[") && step.id.endsWith("]"),
              );
            }

            if (subSubNavigationRecord) {
              result.push(subSubNavigationRecord);
            }
          }
        }
      }

      return result;
    })();

  const setNavigationStateHandler: NavigationStateState["setNavigationState"] =
    (...keys) => {
      const navigationContext = getParsedNavigationContextFromNavigationState(
        navigationState,
        keys,
        currentPath,
      );

      log(
        "green",
        "NavigationStateProvider",
        "setNavigationState",
        keys,
        currentPath,
        navigationContext,
      );

      setNavigationContext(navigationContext ?? []);
    };

  const getNavigationPath = (...keys: string[]): string | null => {
    const path = getPathFromNavigationState(navigationState, keys);

    // console.log(
    //   "[NavigationStateProvider] getNavigationPath",
    //   navigationContext,
    //   keys,
    //   path,
    // );

    return path;
  };

  // Current path change
  // ---------------------

  useEffect(() => {
    log(
      "green",
      "NavigationStateProvider",
      "currentPath changed:",
      currentPath,
    );

    if (!currentPath) {
      return;
    }

    const path =
      currentPath.endsWith("/") && currentPath !== "/"
        ? currentPath.slice(0, -1)
        : currentPath;

    const matchedKeysPath = Object.keys(navigationalPathMap).find((key) => {
      const matched = matchPathWithPattern(path, navigationalPathMap[key]);

      // log(
      //   "green",
      //   "NavigationStateProvider",
      //   "matched?",
      //   path,
      //   navigationalPathMap[key],
      //   matched,
      // );

      return matched;
    });

    if (matchedKeysPath) {
      setNavigationStateHandler(...matchedKeysPath.split("."));
      return;
    } else {
      console.warn("[NavigationStateProvider] No matched keys path", path);
      onPathNotFound?.(currentPath);
    }
  }, [currentPath, navigationalPathMap]);

  // Navigation variants
  // ---------------------

  const isCompactLayout =
    windowSize !== null && windowSize.width < COMPACT_LAYOUT_BREAKPOINT;

  const menuVariant: MenuVariant = isCompactLayout ? "compact" : "default";

  const sideContentVariant: SideContentVariant = isCompactLayout
    ? "compact"
    : "default";

  // Previous navigation state
  // -------------------------

  const prevNavigationContext = useRef<string>("");

  useEffect(() => {
    log(
      "green",
      "NavigationStateProvider",
      "navigationContext updated:",
      "[new]",
      navigationContext,
      "[prev]",
      prevNavigationContext.current,
    );

    if (
      !navigationContext ||
      navigationContext.length === 0 ||
      navigationContext.join(".") === prevNavigationContext.current
    ) {
      log(
        "green",
        "NavigationStateProvider",
        "No navigation state change detected",
        navigationContext,
        prevNavigationContext.current,
      );
      return;
    }

    prevNavigationContext.current = navigationContext.join(".");
    onNavigationContextChange?.(navigationContext);
  }, [navigationContext]);

  // Close side content when menu is open
  // -----------------------------------

  useEffect(() => {
    if (isMenuOpen) {
      setSideContentOpen(false);
    }
  }, [isMenuOpen]);

  useEffect(() => {
    if (isSideContentOpen) {
      setMenuOpen(false);
    }
  }, [isSideContentOpen]);

  // Manual navigation
  // ------------------

  useEffect(() => {
    const unsubscribeNavigate = pubSub.subscribe("navigate", (action) => {
      log(
        "green",
        "NavigationStateProvider",
        "Received navigate action",
        action,
      );

      const processNavigation = () => {
        if (onManualNavigation) {
          const path = getNavigationPath(...action.context);

          log(
            "green",
            "NavigationStateProvider",
            `onManualNavigation: '${action.context.join(", ")}', with path`,
            path,
            action.context,
            action.params,
          );

          if (!path) {
            action?.onPathNotFound?.();
            onPathNotFound?.(action.context.join("/"));
          } else {
            if (action.params) {
              const newPath = addParamsToURL(path, action.params);
              onManualNavigation(action.context, newPath);
            } else {
              onManualNavigation(action.context, path);
            }
          }
        } else {
          setNavigationContext(action.context);
        }
      };

      if (interceptorRef.current) {
        interceptorRef.current.requestNavigation(processNavigation);
      } else {
        processNavigation();
      }
    });

    return () => {
      unsubscribeNavigate();
    };
  }, [pubSub, navigationalPathMap]);

  useEffect(() => {
    const unsubscribe = pubSub.subscribe("menu", (action) => {
      setMenuOpen(action.action === "open");
    });

    return () => {
      unsubscribe();
    };
  }, [pubSub]);

  useEffect(() => {
    if (!pubSub) {
      return;
    }

    const unsubscribeOnPrompt = pubSub.subscribe("onPrompt", () => {
      setSideContentOpen(true);
    });

    return () => {
      unsubscribeOnPrompt();
    };
  }, [pubSub]);

  return (
    <NavigationStateContext.Provider
      value={{
        navigationState,
        navigationalPathMap,
        currentPath,

        menuVariant,
        sideContentVariant,

        activeNavigationRecords,
        activeNavigationRecord,
        navigationContext,

        isMenuOpen,
        setMenuOpen,
        toggleMenu: () => setMenuOpen((prev) => !prev),

        isSideContentOpen,
        setSideContentOpen: (open) => setSideContentOpen(open),

        setNavigationState: setNavigationStateHandler,
        getNavigationPath,
      }}
    >
      {children}
    </NavigationStateContext.Provider>
  );
};
